import { getS, setS } from "./state";

const patterns = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  password: /^(?=.*[A-Z])(?=.*\d).{6,}$/ // example rule
};

/* ===== BUILT-IN RULES ===== */
const rules = {
  required: (value, ctrl) => {
    if (ctrl.required && (value === undefined || value === "")) {
      return `${ctrl.label} is required`;
    }
  },

  maxToday: (value, ctrl) => {
    if (!value) return;

    const selected = new Date(value);
    const today = new Date();

    // remove time for accurate compare
    today.setHours(0, 0, 0, 0);

    if (selected > today) {
      return `${ctrl.label} cannot be in future`;
    }
  }
};
/* ===== HELPERS ===== */

// const getValue = (path, state) =>
//   path.split(".").reduce((o, k) => o?.[k], state);
const getValue = (path, state) => {
  if (!path || typeof path !== "string") return undefined;

  return path.split(".").reduce((o, k) => o?.[k], state);
};

const parseDate = (val) => {
  if (!val) return null;
  return new Date(val);
};

const today = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

/* ===== RULE ENGINE ===== */

const isEffectivelyEmpty = (value) => {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (typeof value === "number") return Number.isNaN(value);
  return false;
};

/** Human-readable pattern failure: reason + how to fix. Optional `patternHint` on control overrides. */
export const buildPatternValidationMessage = (ctrl, value) => {
  const label = ctrl?.label || "This field";
  if (typeof ctrl?.patternHint === "string" && ctrl.patternHint.trim()) {
    return `${label}: ${ctrl.patternHint.trim()}`;
  }

  const pattern = ctrl?.pattern;
  if (!pattern || typeof pattern !== "string") {
    return `${label} does not match the required format.`;
  }

  try {
    // eslint-disable-next-line no-new
    new RegExp(pattern);
  } catch {
    return `${label}: validation rule is misconfigured; contact support.`;
  }

  const raw = String(value ?? "");
  const len = raw.length;

  const dotRange = pattern.match(/^\^\.\{(\d+),(\d+)\}\$$/);
  if (dotRange) {
    const min = Number(dotRange[1]);
    const max = Number(dotRange[2]);
    const tooShort = len < min;
    const tooLong = len > max;
    const sizeHint = tooShort
      ? `Add at least ${min - len} more character(s).`
      : tooLong
        ? `Remove ${len - max} character(s).`
        : "Adjust the text to fit the allowed length.";
    return `${label} must be ${min}–${max} characters (you have ${len}). ${sizeHint}`;
  }

  if (pattern === "^[0-9]{10}$") {
    return `${label} must be exactly 10 digits (no spaces or dashes). Example: 9876543210.`;
  }

  if (pattern === "^[0-9]{6}$") {
    return `${label} must be exactly 6 digits (no spaces).`;
  }

  if (pattern === "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$") {
    return `${label} must look like name@domain.com with no spaces around @.`;
  }

  if (pattern === "^[A-Za-z0-9][A-Za-z0-9 &()'.,-]{2,79}$") {
    return `${label}: use 3–80 characters. Start with a letter or number; after that you may use letters, numbers, spaces, and & ( ) ' . , - only. Remove leading symbols or characters outside this set.`;
  }

  if (pattern === "^(?:0|[1-9]\\d*)(?:\\.\\d{1,2})?$") {
    const minN = ctrl.min != null && ctrl.min !== "" ? Number(ctrl.min) : null;
    const maxN = ctrl.max != null && ctrl.max !== "" ? Number(ctrl.max) : null;
    const bound =
      Number.isFinite(minN) && Number.isFinite(maxN)
        ? ` Allowed range for this field is ${minN}–${maxN}.`
        : "";
    return `${label} must be a non-negative number with at most 2 decimal places (e.g. 0, 1299, or 1299.50). Avoid values like 01.50 (leading zeros except a single 0).${bound}`;
  }

  if (pattern === "^\\s*$|^\\{[\\s\\S]*\\}$") {
    return `${label}: leave the field empty or paste one valid JSON object using curly braces {...}.`;
  }

  return `${label} does not match the required format. Adjust the value to match the field rules (length, digits, or allowed symbols).`;
};

const ruleHandlers = {
  required: (value, ctrl) => {
    if (isEffectivelyEmpty(value)) return `${ctrl.label} is required`;
  },

  min: (value, rule) => {
    if (!value) return;
    const v = parseDate(value);
    const minVal = rule.value === "today"
      ? today()
      : parseDate(rule.value);

    if (v < minVal) {
      return `Date should be after ${rule.value}`;
    }
  },

  max: (value, rule) => {
    if (!value) return;
    const v = parseDate(value);
    const maxVal = rule.value === "today"
      ? today()
      : parseDate(rule.value);

    if (v > maxVal) {
      return `Date should be before ${rule.value}`;
    }
  },

  minField: (value, rule, state) => {
    if (!value) return;

    const v = parseDate(value);
    // const ref = parseDate(getValue(rule.field, state));
    const ref = rule.field ? parseDate(getValue(rule.field, state)) : null;

    if (ref && v < ref) {
      return `Date should be after ${rule.field}`;
    }
  },

  maxField: (value, rule, state) => {
    if (!value) return;

    const v = parseDate(value);
    // const ref = parseDate(getValue(rule.field, state));
    const ref = rule.field ? parseDate(getValue(rule.field, state)) : null;

    if (ref && v > ref) {
      return `Date should be before ${rule.field}`;
    }
  }
};

/* ===== MAIN VALIDATOR ===== */

export const validate = (controls) => {
  const s = getS();
  const errors = {};


  
  controls.forEach(ctrl => {
    if (!ctrl.bind) return;   // 🔥 skip non-form controls
    if (ctrl.type === "uploadapi") return; // ZIP import is optional when editing prices/metadata
    const value = getValue(ctrl.bind, s);

    // required shortcut
    if (ctrl.required) {
      const msg = ruleHandlers.required(value, ctrl);
      if (msg) {
        errors[ctrl.id] = msg;
        return;
      }
    }

    // rule-based validation
    ctrl.rules?.forEach(rule => {
      const handler = ruleHandlers[rule.type];
      if (!handler) return;

      const msg = handler(value, rule, s);
      if (msg) {
        errors[ctrl.id] = msg;
      }
    });
    if (ctrl.dataType === "email" && value) {
      if (!patterns.email.test(value)) {
        errors[ctrl.id] =
          `${ctrl.label || "Email"} must look like name@domain.com (no spaces).`;
      }
    }

    if (ctrl.dataType === "password" && value) {
      if (!patterns.password.test(value)) {
        errors[ctrl.id] =
          `${ctrl.label || "Password"} must be at least 6 characters and include one uppercase letter and one digit.`;
      }
    }

    if (ctrl.pattern && !isEffectivelyEmpty(value)) {
      let testStr;
      if (typeof value === "string") {
        testStr = value;
      } else if (value && typeof value === "object" && !Array.isArray(value)) {
        try {
          testStr = JSON.stringify(value);
        } catch {
          testStr = String(value);
        }
      } else {
        testStr = String(value);
      }
      try {
        const regex = new RegExp(ctrl.pattern);
        if (!regex.test(testStr)) {
          errors[ctrl.id] = buildPatternValidationMessage(ctrl, value);
        }
      } catch {
        errors[ctrl.id] = `${ctrl.label || "Field"}: validation rule is misconfigured.`;
      }
    }
  });

  setS({ errors });

  return Object.keys(errors).length === 0;
};
// export const validate = (controls) => {
//   const s = getS();
//   const errors = {};

//   controls.forEach(ctrl => {
//     const value = ctrl.bind
//       .split(".")
//       .reduce((o, k) => o?.[k], s);

//     // run all rules
//     Object.keys(rules).forEach(rule => {
//       if (ctrl[rule]) {
//         const msg = rules[rule](value, ctrl, s);
//         if (msg) errors[ctrl.id] = msg;
//       }
//     });
//   });

//   setS({ errors });

//   return Object.keys(errors).length === 0;
// };

// export const validate = (controls) => {
//   const s = getS();
//   const errors = {};

//   controls.forEach(ctrl => {
//     const value = ctrl.bind
//       .split(".")
//       .reduce((o, k) => o?.[k], s);

//     // REQUIRED
//     if (ctrl.required && (value === undefined || value === "" || value === false)) {
//       errors[ctrl.id] = `${ctrl.label} is required`;
//     }

//     // CUSTOM VALIDATION
//     if (ctrl.validate) {
//       const msg = ctrl.validate(value, s);
//       if (msg) errors[ctrl.id] = msg;
//     }
//   });

//   setS({ errors });

//   return Object.keys(errors).length === 0;
// };